import {
  format as formatFns,
  formatDistanceToNow,
  parseISO,
  type FormatOptions,
} from 'date-fns'

/**
 * Last-resort currency for display formatting. This is the ONE hardcoded
 * currency literal on the frontend — everything else resolves from the org's
 * `settings.defaultCurrency` via {@link useOrgCurrency}. Used only when a
 * record/query carries no currency and no org currency is available.
 */
export const FALLBACK_CURRENCY = 'USD'

/**
 * Formats a monetary amount with the given ISO currency code, e.g.
 * `formatCurrency(1234.5, 'PKR')` → "PKR 1,234.5" (symbol/placement per the
 * runtime's `en-US` conventions). Callers pass the currency resolved from the
 * record or the org settings; an empty/invalid code falls back to
 * {@link FALLBACK_CURRENCY}. Never throws — an unknown code degrades to
 * "<code> <amount>".
 */
export function formatCurrency(
  amount: number,
  currency: string | null | undefined,
  options?: Intl.NumberFormatOptions
): string {
  const code = currency?.trim() || FALLBACK_CURRENCY
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: code,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
      ...options,
    }).format(amount)
  } catch {
    return `${code} ${amount.toFixed(2)}`
  }
}

/**
 * Formats a date string or Date for display.
 */
export function formatDate(
  date: string | Date,
  formatStr: string = 'PP',
  options?: FormatOptions
): string {
  const d = typeof date === 'string' ? parseISO(date) : date
  return formatFns(d, formatStr, options)
}

/**
 * Builds the small secondary line shown beneath a user's name in dropdowns,
 * e.g. "EMP-1042 · zeeshan@acme.com". Joins whichever parts are present with
 * " · " and returns undefined when neither is available.
 */
export function userSubline(
  employeeId?: string | null,
  email?: string | null
): string | undefined {
  const parts = [employeeId, email].filter(
    (p): p is string => !!p && p.trim().length > 0
  )
  return parts.length ? parts.join(' · ') : undefined
}

/**
 * Formats an ISO date-time string for display (date and time).
 */
export function formatDateTime(
  isoString: string,
  formatStr: string = 'PPp'
): string {
  return formatDate(isoString, formatStr)
}

/**
 * Formats a date as relative time (e.g. "2 hours ago").
 */
export function formatRelative(date: string | Date, baseDate?: Date): string {
  const d = typeof date === 'string' ? parseISO(date) : date
  return formatDistanceToNow(d, { addSuffix: true, ...(baseDate && { addSuffix: true }) })
}
